import { forwardRef, memo, useImperativeHandle, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { TextEditor } from '@chhjit/react-components';
import { normalizeHtmlContent } from '@hunkware/shared/utils';

import { useTextEditorForm } from './hooks';
import { TextEditorFormHandle, TextEditorFormProps } from './types';
import { getProcessedValue } from './utils';

export const TextEditorForm = memo(
  forwardRef<TextEditorFormHandle, TextEditorFormProps>(
    ({ initialValues, editor, disabled, onFormValuesChange, onFormStateChange }, ref) => {
      const { control, getValues, handleSubmit } = useTextEditorForm({
        initialValues,
        onFormValuesChange,
        onFormStateChange,
      });

      useImperativeHandle(ref, () => ({
        getValues,
        handleFormSubmit: handleSubmit,
      }));

      const defaultButtonList = useMemo(
        () =>
          [
            ['bold', 'italic', 'strike', 'underline', 'removeFormat'],
            ['align', 'list', 'hiliteColor', 'fontColor'],
            ['formatBlock'],
            ['fontSize'],
            ['undo', 'redo', 'fullScreen'],
          ].map(list => list.filter(button => !editor?.removeButtons?.includes(button))),
        [editor?.removeButtons],
      );

      return (
        <Controller
          control={control}
          name="text"
          render={({ field: { value, onChange, ...rest } }) => (
            <TextEditor
              {...editor?.props}
              {...rest}
              value={getProcessedValue(value)}
              options={{
                defaultTag: 'div',
                buttonList: defaultButtonList,
              }}
              disabled={disabled}
              onChange={content => onChange(normalizeHtmlContent(content as string))}
            />
          )}
        />
      );
    },
  ),
);
